import { Fragment, memo, useEffect, useState } from 'react'
import { ChevronRight, Eye, Loader2, MessageSquarePlus } from 'lucide-react'
import { highlightScanMatch } from '@/shared/office/word'
import { useScanActions } from '@/screens/scan/ScanActionsContext'
import { resultKey, verdictColor, verdictModifier, type SelectedHighlight } from '@/screens/scan/lib/sessionDisplay'
import type { ScanResult } from '@/screens/scan/types'

/**
 * One scan result: verdict dot + name, expanding inline to its explanation and
 * matched text blocks. A separate View button opens the full detail screen.
 */
function ResultRowComponent({
  result,
  name,
  selectedBlock,
  onSelectBlock,
  onView
}: {
  result: ScanResult
  name: string
  selectedBlock: SelectedHighlight | null
  onSelectBlock: (next: SelectedHighlight | null) => void
  onView: () => void
}) {
  const { sendToChat } = useScanActions()
  const [expanded, setExpanded] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const rowKey = resultKey(result)
  const blocks = result.textBlocks ?? []
  const isLoading = result.status === 'INITIAL'
  const color = verdictColor(result.scanStatus)
  const expandable = !isLoading
  const loadingHighlights = expanded && !result.highlightsLoaded && !!result._id

  useEffect(() => {
    if (expanded || selectedBlock?.resultKey !== rowKey) return
    onSelectBlock(null)
  }, [expanded, onSelectBlock, rowKey, selectedBlock?.resultKey])

  const onBlockClick = async (index: number, text: string) => {
    setNotFound(false)
    const isActive = selectedBlock?.resultKey === rowKey && selectedBlock.blockIndex === index
    if (isActive) {
      onSelectBlock(null)
      return
    }
    const located = await highlightScanMatch(text).catch(() => false)
    if (located) {
      onSelectBlock({ resultKey: rowKey, blockIndex: index })
    } else {
      onSelectBlock(null)
      setNotFound(true)
    }
  }

  return (
    <div className='ramp-scan-result'>
      <div className='ramp-scan-result-head'>
        <button
          type='button'
          className={`ramp-scan-result-toggle${expandable ? '' : ' is-static'}`}
          onClick={() => expandable && setExpanded((value) => !value)}
        >
          {isLoading ? (
            <Loader2 size={13} className='ramp-scan-result-chevron ramp-spin' />
          ) : (
            <ChevronRight size={13} className={`ramp-scan-result-chevron${expanded ? ' is-open' : ''}`} />
          )}
          <span className='ramp-scan-status'>
            {color ? <span className='ramp-scan-dot' style={{ background: color }} /> : null}
          </span>
          <span className='ramp-scan-result-name'>{name}</span>
        </button>
        {!isLoading ? (
          <button type='button' className='ramp-scan-view-btn' onClick={onView}>
            <Eye size={14} /> View
          </button>
        ) : null}
      </div>

      {expanded && expandable ? (
        <div className='ramp-scan-blocks'>
          {loadingHighlights ? (
            <div className='ramp-scan-empty'>
              <Loader2 size={14} className='ramp-spin' /> Loading findings…
            </div>
          ) : (
            <>
              {result.explanation ? <p className='ramp-scan-explanation'>{result.explanation}</p> : null}
              {blocks.map((block, index) => {
                const isActive = selectedBlock?.resultKey === rowKey && selectedBlock.blockIndex === index
                return (
                  <Fragment key={`${result.checklistItemId}-${index}`}>
                    <div
                      className={['ramp-scan-block', verdictModifier(result.scanStatus), isActive ? 'is-active' : '']
                        .filter(Boolean)
                        .join(' ')}
                      style={color ? { borderLeftColor: color } : undefined}
                      title='Click to find and highlight in the document'
                      onClick={() => onBlockClick(index, block.text)}
                    >
                      {block.text || '[No applicable language found]'}
                    </div>
                    {index !== blocks.length - 1 ? <div className='ramp-scan-block-sep' /> : null}
                  </Fragment>
                )
              })}
              {!blocks.length && !result.explanation ? (
                <p className='ramp-scan-explanation'>No applicable language found.</p>
              ) : null}
              {notFound ? (
                <div className='ramp-scan-error'>Couldn’t locate that text in the current document.</div>
              ) : null}
              <button type='button' className='ramp-scan-primary-sm' onClick={() => sendToChat(result, name)}>
                <MessageSquarePlus size={14} /> Add to chat
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}

export const ResultRow = memo(ResultRowComponent)
